import React from 'react'
import getConfig from 'next/config'
import Router, { useRouter } from 'next/router'
import { getPageOptions } from '@jarvis.oddle/core/tools/customisation'
import useAppSelector from 'src/hooks/useAppSelector'
import { updateCustomisationSetting } from 'src/redux/customisation/action'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import { isObjectEmpty } from 'src/utils/object'
import { isEmpty } from 'src/utils/string'
import { makeQueryString, parseUrl } from 'src/utils/routes'
import { selectDetailMenuSite } from 'src/redux/meta'

const { publicRuntimeConfig } = getConfig()

const FrameConnector = ({
  enabled,
  children
}: {
  enabled: boolean
  children
}): JSX.Element => {
  const router = useRouter()
  const dispatch = useAppDispatch()
  const { currentAction, sectionSettingData } = useAppSelector(
    (state) => state['@customisation'].data
  )
  const metaState = useAppSelector(selectDetailMenuSite)
  const { enableNewCheckoutFlow } = metaState?.features || {}

  React.useEffect(() => {
    if (enabled) {
      window.addEventListener('message', dispatchActionFromParentFrame)
      Router.events.on('routeChangeStart', onRouteChangeStart)
      return () => {
        Router.events.off('routeChangeStart', onRouteChangeStart)
        window.removeEventListener('message', dispatchActionFromParentFrame)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const didMountRef = React.useRef(currentAction)

  React.useEffect(() => {
    if (
      didMountRef.current !== currentAction &&
      currentAction.type === '@@preview/CHANGE_PAGE'
    ) {
      didMountRef.current = currentAction
      switch (currentAction.payload.pageId) {
        case 'product':
          Router.push(
            `/${router.query.locale}${makeQueryString({
              productId: 'preview',
              previewMode: true,
              previewToken: currentAction.payload.previewToken
            })}`
          )
          break
        case 'checkout':
          Router.push(
            `/${router.query.locale}/${
              currentAction.payload.pageId
            }/preview${makeQueryString({
              previewMode: true,
              previewToken: currentAction.payload.previewToken,
              ...(enableNewCheckoutFlow
                ? {
                    step_address: true
                  }
                : {})
            })}`
          )
          break
        case 'invoice':
          Router.push(
            `/${router.query.locale}/${
              currentAction.payload.pageId
            }/preview/preview${makeQueryString({
              previewMode: true,
              previewToken: currentAction.payload.previewToken
            })}`
          )
          break
        case 'index':
          Router.push(
            `/${router.query.locale}${makeQueryString({
              previewMode: true,
              previewToken: currentAction.payload.previewToken
            })}`
          )
          break
        default:
          Router.push(
            `/${router.query.locale}/${
              currentAction.payload.pageId
            }${makeQueryString({
              previewMode: true,
              previewToken: currentAction.payload.previewToken
            })}`
          )
          break
      }
    }
  }, [currentAction, enableNewCheckoutFlow, router.query])

  const sendMessageToParentFrame = (message) => {
    enabled &&
      window.parent.postMessage(
        message,
        publicRuntimeConfig.CUSTOMISATION_BASE_URL
      )
  }

  const onRouteChangeStart = (_url: string) => {
    const url = parseUrl(_url)
    const { canNavigateFromCustomisationUI } = getPageOptions({
      query: url,
      sectionSettingData
    })
    if (canNavigateFromCustomisationUI === false) return
    sendMessageToParentFrame({
      type: '@@preview/PREVIEW_FRAME_ROUTE_CHANGE_START',
      payload: { url }
    })
  }

  const dispatchActionFromParentFrame = (event) => {
    // Chrome browser on iOS version 12 will send an event on every user interaction on behalf
    // of the parent origin https://stackoverflow.com/questions/54079756/internal-chromium-postmessage-event
    // (no official documentation found yet).

    // Therefore, to prevent Oddle Shop to rerender with an unnecessary data which leads to a crash,
    // only dispatch the action if the event is fired from Oddle Shop Editor
    // which is distinguished by the non-empty type field in the currentAction object
    if (
      event.origin === publicRuntimeConfig.CUSTOMISATION_BASE_URL &&
      !isObjectEmpty(event.data.currentAction) &&
      !isEmpty(event.data.currentAction.type)
    ) {
      dispatch(updateCustomisationSetting(event.data))
    }
  }

  return children
}

export default FrameConnector
